import { createContext, useContext, useEffect, useMemo, useState } from "react";
import {
  GRID_CONFIGS,
  INVOICING_ROUTES,
  RECONCILIATION_STEPS,
} from "../accountingConstants";
import { useReconcileViewModel } from "./useReconcileViewModel";
import { useVerificationState } from "./useVerificationState";

export const ReconcileContext = createContext(null);

export const ReconcileProvider = ({ orderId, viewMode, children }) => {
  const reconcileViewModel = useReconcileViewModel({ orderId });
  const [invoiceFormsState, setInvoiceFormsState] = useState({});
  const isInvoiceMode = viewMode === "invoice";

  const [activeStep, setActiveStep] = useState(
    viewMode === INVOICING_ROUTES.PRE_BILL
      ? RECONCILIATION_STEPS.RECONCILIATION
      : null
  );

  const getVarianceColor = (variance) => {
    if (variance < 0) {
      return "error.main";
    }
    if (variance > 0) {
      return "success.main";
    }
    return "text.primary";
  };

  const getGridConfig = (mode = null) => {
    if (mode === "variance") {
      return GRID_CONFIGS.VARIANCE_ITEM;
    }
    if (mode === "payment") {
      return GRID_CONFIGS.PAYMENT_ITEM;
    }
    return GRID_CONFIGS.INVOICE_ITEM;
  };

  const {
    getOrder,
    getOrderTemplates,
    getPurchaseOrders,
    order,
    fetchAndEnsureDraftInvoice,
    invoicesModule,
    purchaseOrders,
  } = reconcileViewModel;

  useEffect(() => {
    getOrder();
    getOrderTemplates();
  }, []);

  const updateInvoiceFormState = (invoiceItemId, formState) => {
    setInvoiceFormsState((prev) => {
      if (formState == null) {
        const { [invoiceItemId]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [invoiceItemId]: formState };
    });
  };

  const purchaseOrderOptions = useMemo(() => {
    return purchaseOrders
      .filter((po) => po.orderTemplateId === order?.orderTemplateId)
      .map((po) => ({
        label: `${po.number} ${po.voidDate ? "(Voided)" : ""}`,
        value: po.id,
      }));
  }, [purchaseOrders, order]);

  useEffect(() => {
    if (order?.homeId) {
      getPurchaseOrders();
    }
  }, [order?.homeId]);

  useEffect(() => {
    if (order?.id && order?.orderTemplateName) {
      fetchAndEnsureDraftInvoice();
      if (viewMode !== INVOICING_ROUTES.INVOICED) {
        invoicesModule.getOrderVariances(); // not sure if needed in invoice view
      }
    }
  }, [order?.id, order?.orderTemplateName]);

  const verificationManager = useVerificationState();

  const value = useMemo(
    () => ({
      ...reconcileViewModel,
      activeStep,
      setActiveStep,
      viewMode,
      isInvoiceMode,
      getGridConfig,
      getVarianceColor,
      purchaseOrderOptions,
      invoiceFormsState,
      updateInvoiceFormState,
      ...verificationManager,
    }),
    [reconcileViewModel, activeStep, viewMode, verificationManager]
  );

  return (
    <ReconcileContext.Provider value={value}>
      {children}
    </ReconcileContext.Provider>
  );
};

export const useReconcileContext = () => {
  const context = useContext(ReconcileContext);
  if (!context) {
    throw new Error(
      "useReconcileContext must be used within a ReconcileProvider"
    );
  }
  return context;
};
